import { Link } from "react-router-dom";
import { Check, X, Camera, Sun, Shirt, ArrowUpRight } from "lucide-react";

const SAMPLES = [
  { url: "https://images.pexels.com/photos/1239288/pexels-photo-1239288.jpeg?auto=compress&cs=tinysrgb&w=600&q=85", label: "Front face — eyes to camera" },
  { url: "https://images.pexels.com/photos/2065200/pexels-photo-2065200.jpeg?auto=compress&cs=tinysrgb&w=600&q=85", label: "Full body — relaxed standing" },
  { url: "https://images.pexels.com/photos/1689731/pexels-photo-1689731.jpeg?auto=compress&cs=tinysrgb&w=600&q=85", label: "Side profile — neutral" },
  { url: "https://images.pexels.com/photos/2876032/pexels-photo-2876032.jpeg?auto=compress&cs=tinysrgb&w=600&q=85", label: "Three-quarter — natural" },
];

export default function ApplicationGuide() {
  return (
    <div data-testid="guide-page" className="pt-32">
      <section className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">Open Casting / How to apply</p>
        <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light mt-6 leading-[0.9]">
          Application<br/><span className="italic text-zinc-600">photo guide.</span>
        </h1>
        <p className="text-zinc-700 mt-8 max-w-2xl text-lg leading-relaxed">
          Great photos for an application aren't fancy — they're honest. We need to see your
          face, body, and posture clearly. Follow the guide below to give us our best chance
          to recognise your potential.
        </p>

        {/* WHAT TO SHOOT */}
        <div className="mt-20">
          <p className="label-overline text-zinc-500">What we need</p>
          <h2 className="font-serif text-4xl mt-3">Four photos.</h2>
          <p className="text-zinc-600 mt-2">Submit at least two — ideally all four.</p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {SAMPLES.map((s, i) => (
              <div key={i} className="border border-black/10 bg-white">
                <div className="aspect-[3/4] overflow-hidden bg-zinc-100">
                  <img src={s.url} alt={s.label} className="w-full h-full object-cover"/>
                </div>
                <div className="p-3">
                  <p className="label-overline text-zinc-500">No. {String(i + 1).padStart(2, "0")}</p>
                  <p className="text-sm font-serif mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RULES */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          <div className="bg-white p-10">
            <p className="label-overline text-emerald-700 inline-flex items-center gap-2"><Check size={14}/> Do</p>
            <ul className="mt-6 space-y-4 text-zinc-700">
              <li className="flex gap-3"><Sun size={18} className="shrink-0 text-emerald-700 mt-1"/> Shoot in natural daylight (next to a window, midday).</li>
              <li className="flex gap-3"><Camera size={18} className="shrink-0 text-emerald-700 mt-1"/> Phone camera is fine. Hold horizontal? portrait. Steady.</li>
              <li className="flex gap-3"><Shirt size={18} className="shrink-0 text-emerald-700 mt-1"/> Wear plain neutral clothing — fitted, not baggy.</li>
              <li className="flex gap-3"><Check size={18} className="shrink-0 text-emerald-700 mt-1"/> Keep makeup minimal or skip entirely. Hair off your face.</li>
              <li className="flex gap-3"><Check size={18} className="shrink-0 text-emerald-700 mt-1"/> Plain wall, simple background, no clutter.</li>
              <li className="flex gap-3"><Check size={18} className="shrink-0 text-emerald-700 mt-1"/> Eyes to camera. Relaxed expression. Stand tall, normal posture.</li>
            </ul>
          </div>
          <div className="bg-white p-10">
            <p className="label-overline text-rose-700 inline-flex items-center gap-2"><X size={14}/> Don't</p>
            <ul className="mt-6 space-y-4 text-zinc-700">
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> No filters, no Snapchat overlays, no Instagram presets.</li>
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> No heavy retouching, lash extensions, or contouring.</li>
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> No sunglasses, no hats, no bags or accessories.</li>
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> No professional studio shots — we want to see *you*, not a stylist.</li>
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> Avoid backlight, shadows over face, low light, blur.</li>
              <li className="flex gap-3"><X size={18} className="shrink-0 text-rose-700 mt-1"/> No mirror selfies for the front face shot.</li>
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 bg-black text-white p-12 lg:p-16">
          <p className="label-overline text-zinc-300">Ready?</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter font-light mt-4 leading-[0.95]">
            Submit your application.
          </h2>
          <p className="text-zinc-300 mt-4 max-w-xl">
            We review every submission. If your profile aligns with our roster we'll be in touch within 14 days.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-hero-light">Apply now <ArrowUpRight size={16}/></Link>
            <Link to="/status" className="btn-hero-outline">Check application status</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
